import { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import LoadingIndicator from '@components/LoadingIndicator/LoadingIndicator';
import OrderFormContainer from '@components/RequisitionCreate/OrderFormContainer/OrderFormContainer';
import { DRAFT_ORDERS, PATIENTS } from '@constants/routes';
import { RerequisitionType } from '@customTypes/requisition';
import { useActiveReanalysisDraftForRequisition, usePatientDetail } from '@hooks';
import { getDraftOrderDetail } from '@store/actions';

const RequisitionJSONSchemaForm = lazy(
  () => import('@components/RequisitionCreate/RequisitionJSONSchemaForm/RequisitionJSONSchemaForm'),
);

export const OrderReanalysis: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const patientId = useMemo(() => params?.id, [params]);
  const requisitionId = useMemo(() => params?.reqId, [params]);
  const campaignCode = useMemo(() => searchParams.get('campaign'), [searchParams]) ?? undefined;
  const productIds = useMemo(() => searchParams.getAll('product_ids'), [searchParams]);
  const { patientName, loading } = usePatientDetail(patientId);
  const [draftRedirectResolved, setDraftRedirectResolved] = useState(false);

  const { draftId: activeReanalysisDraftId, refetch: refetchActiveReanalysisDraft } =
    useActiveReanalysisDraftForRequisition(requisitionId);

  useEffect(() => {
    if (!activeReanalysisDraftId || !patientId) {
      setDraftRedirectResolved(true);
      return;
    }

    let cancelled = false;
    setDraftRedirectResolved(false);

    getDraftOrderDetail(activeReanalysisDraftId)
      .then(() => {
        if (!cancelled) {
          navigate(`${DRAFT_ORDERS}/${activeReanalysisDraftId}`, {
            replace: true,
            state: { prevPath: `${PATIENTS}/${patientId}` },
          });
        }
      })
      .catch(async () => {
        if (!cancelled) {
          await refetchActiveReanalysisDraft();
          setDraftRedirectResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeReanalysisDraftId, navigate, patientId, refetchActiveReanalysisDraft]);

  const moveToPatientDetail = useCallback(() => {
    navigate(`/patients/${patientId}`);
  }, [patientId, navigate]);

  const breadcrumbs = useMemo(
    () => [
      {
        onClick: moveToPatientDetail,
        children: patientName || (loading ? 'Loading…' : ''),
      },
      {
        active: true,
        children: 'Order Reanalysis',
      },
    ],
    [moveToPatientDetail, patientName, loading],
  );

  if (activeReanalysisDraftId && !draftRedirectResolved) {
    return (
      <Stack className='align-items-center py-5'>
        <LoadingIndicator />
      </Stack>
    );
  }

  return (
    <OrderFormContainer
      cancelReturnsToPatient
      enableSaveAsDraft
      rerequisitionFrom={requisitionId}
      rerequisitionType={RerequisitionType.REANALYSIS}
      breadcrumbs={breadcrumbs}
      title='Order Reanalysis'
      onCancel={moveToPatientDetail}
    >
      {loading ? (
        <Stack className='align-items-center py-5'>
          <LoadingIndicator />
        </Stack>
      ) : (
        <RequisitionJSONSchemaForm
          initialProductIds={productIds}
          rerequisitionType={RerequisitionType.REANALYSIS}
          selectedCampaignCode={campaignCode}
          selectedRequisitionId={requisitionId}
        />
      )}
    </OrderFormContainer>
  );
};
